import React from 'react';
import { NavLink as RouterNavLink, Outlet, useNavigate } from 'react-router-dom';
import { 
    FaTachometerAlt, FaUser, FaPlusCircle, FaBriefcase, FaSignOutAlt, 
    FaEnvelope, FaWallet, FaLeaf, FaBars, FaTimes, FaUsers
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import loadImage from '../assets/imageLoader';

const BuyerLayout = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

    const handleLogout = () => {
        logout(navigate);
    };

    const NavItem = ({ to, icon, label, exact }) => (
        <RouterNavLink
            to={to}
            end={exact}
            onClick={() => setIsMobileMenuOpen(false)}
            className={({ isActive }) =>
                `flex items-center px-4 py-3 mx-3 mb-2 rounded-xl transition-all duration-200 font-bold ${
                    isActive 
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' 
                    : 'text-slate-500 hover:bg-emerald-50 hover:text-emerald-600'
                }`
            }
        >
            <span className="text-xl">{icon}</span>
            <span className="ml-3 text-sm">{label}</span>
        </RouterNavLink>
    );

    return (
        <div className="min-h-screen bg-slate-50 flex font-sans">
            {/* Mobile Header */}
            <div className="lg:hidden fixed w-full bg-white z-20 shadow-sm px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-emerald-600 font-black text-xl">
                    <FaLeaf /> <span>Krishok Lagbe</span>
                </div>
                <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-slate-600 text-2xl">
                    {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
                </button>
            </div>

            {/* Sidebar */}
            <aside className={`
                fixed lg:static top-0 left-0 h-full w-72 bg-white border-r border-slate-200 z-30 transform transition-transform duration-300 ease-in-out
                ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="flex flex-col h-full">
                    {/* Brand */}
                    <div className="h-20 flex items-center px-8 border-b border-slate-100">
                        <div className="flex items-center gap-2 text-emerald-600 font-black text-2xl tracking-tight">
                            <FaLeaf /> <span>Krishok Lagbe</span>
                        </div>
                    </div>

                    {/* User Profile Card */}
                    <div className="p-6">
                        <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 flex items-center gap-3">
                            <img 
                                src={loadImage(user?.picture)} 
                                alt="Profile" 
                                className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                            />
                            <div className="overflow-hidden">
                                <h3 className="font-bold text-slate-800 text-sm truncate">{user?.name || 'User'}</h3>
                                <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">{user?.role || 'Buyer'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-grow overflow-y-auto custom-scrollbar">
                        <p className="px-8 mb-2 text-xs font-black text-slate-300 uppercase tracking-widest">Menu</p>
                        <NavItem to="/owner-dashboard" icon={<FaTachometerAlt />} label="Dashboard" exact />
                        <NavItem to="/publish-job" icon={<FaPlusCircle />} label="Post a Job" />
                        <NavItem to="/my-jobs" icon={<FaBriefcase />} label="My Jobs" />
                        <NavItem to="/my-hires" icon={<FaUsers />} label="Hired Workers" />
                        <NavItem to="/messages" icon={<FaEnvelope />} label="Messages" />
                        <NavItem to="/wallet" icon={<FaWallet />} label="Wallet" />
                        
                        <div className="mt-8">
                            <p className="px-8 mb-2 text-xs font-black text-slate-300 uppercase tracking-widest">Settings</p>
                            <NavItem to="/owner-profile" icon={<FaUser />} label="My Profile" />
                        </div>
                    </nav>

                    {/* Footer / Logout */}
                    <div className="p-6 border-t border-slate-100">
                        <button 
                            onClick={handleLogout}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-slate-500 font-bold hover:bg-red-50 hover:text-red-500 transition-colors"
                        >
                            <FaSignOutAlt />
                            <span>Sign Out</span>
                        </button>
                    </div>
                </div>
            </aside>

            {/* Overlay for mobile */}
            {isMobileMenuOpen && (
                <div 
                    className="fixed inset-0 bg-black/20 z-20 lg:hidden"
                    onClick={() => setIsMobileMenuOpen(false)}
                ></div>
            )}

            {/* Main Content */}
            <main className="flex-1 w-full lg:w-auto mt-16 lg:mt-0 p-4 lg:p-8 overflow-y-auto h-screen custom-scrollbar">
                <div className="max-w-6xl mx-auto">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default BuyerLayout;